import { useRef, useState, useMemo, useEffect } from 'react';
import useSWR from 'swr';
import toast from 'react-hot-toast';
import { useAuthStore } from '../hooks/useAuth';
import { useLocation } from 'wouter';
import { useUIStore } from '../hooks/useUIStore';
import { formatDateInTimeZone, formatInWarehouseTimeZone } from '../utils/timezoneDisplay';
import { api } from '../lib/api';
import { CompleteMissingInfoModal } from '../components/carrier/CompleteMissingInfoModal';
import { Badge } from '../components/ui/badge';
import {
    Calendar,
    MapPin,
    MessageSquare,
    Plus,
    Truck,
    AlertCircle,
    CheckCircle,
    ChevronRight,
    Search,
    LogOut
} from 'lucide-react';

interface Appointment {
    id: string;
    warehouseName: string;
    warehouseTimeZone?: string;
    customerPoNumber: string;
    requestedDate: string;
    confirmedDate?: string;
    status: 'pending' | 'approved' | 'checked_in' | 'in_progress' | 'completed' | 'cancelled';
    dockId?: string;
    referenceCode: string;
    unreadMessagesCount?: number;
    driverName?: string;
    carrierName?: string;
    agentDecision?: string;
    agentDecisionReason?: Array<{ check: string; passed: boolean; detail: string }>;
    truckNumber?: string;
    trailerNumber?: string;
    trailerType?: string;
    estimatedPalletCount?: number;
    carrierNotes?: string;
    carrierEmail?: string;
    confirmationCode?: string;
    cancelReason?: string;
    notes?: string;
}

export function CarrierDashboard() {
    const { token, user, logout } = useAuthStore();
    const [, setLocation] = useLocation();

    // Local UI State for Filtering
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [completeModalAppt, setCompleteModalAppt] = useState<Appointment | null>(null);
    const [showClaimForm, setShowClaimForm] = useState(false);
    const [claimCode, setClaimCode] = useState('');
    const [claimPo, setClaimPo] = useState('');
    const [claiming, setClaiming] = useState(false);
    const [cancellingId, setCancellingId] = useState<string | null>(null);
    const [signingOut, setSigningOut] = useState(false);

    const handleSignOut = async () => {
        if (signingOut) return;
        setSigningOut(true);
        try {
            const ok = await logout();
            if (ok) {
                setLocation('/carrier-login');
            }
        } finally {
            setSigningOut(false);
        }
    };

    // Previous data ref for comparison
    const prevAppointmentsRef = useRef<Appointment[]>([]);
    const { openAppointmentDetail } = useUIStore();

    const handleAppointmentClick = (appt: Appointment) => {
        openAppointmentDetail({
            ...appt,
            po_number: appt.customerPoNumber,
            carrier_name: user?.companyName || 'Carrier', // Carrier view implies self
            scheduled_time: appt.confirmedDate || appt.requestedDate,
            dock_id: appt.dockId
        });
    };

    const { data, error, isLoading, mutate } = useSWR(
        token ? ['carrier-appointments', token] : null,
        () => api.getCarrierAppointments(),
        {
            refreshInterval: 20000,
            revalidateOnFocus: true,
            revalidateOnMount: true,
            onSuccess: (newData) => {
                const newAppts: Appointment[] = newData.appointments || [];
                const prevAppts = prevAppointmentsRef.current;

                if (prevAppts.length > 0) {
                    // Check for NEW appointments
                    const newIds = new Set(newAppts.map(a => a.id));
                    const prevIds = new Set(prevAppts.map(a => a.id));
                    const addedCount = [...newIds].filter(x => !prevIds.has(x)).length;

                    if (addedCount > 0) {
                        toast.success('🔔 New appointment added!');
                    }


                    // Optimization: Use Map for O(1) lookup
                    const prevApptsMap = new Map(prevAppts.map(a => [a.id, a]));

                    // Check for STATUS changes
                    newAppts.forEach(newAppt => {
                        const oldAppt = prevApptsMap.get(newAppt.id);

                        if (oldAppt) {
                            if (oldAppt.status !== newAppt.status) {
                                if (newAppt.status === 'approved') {
                                    toast.success(`✅ PO #${newAppt.customerPoNumber} Approved!`);
                                } else if (newAppt.status === 'checked_in') {
                                    toast.success(`🚛 PO #${newAppt.customerPoNumber} Checked In`);
                                }
                            }

                            // Check for new unread messages
                            const prevUnread = oldAppt.unreadMessagesCount || 0;
                            const newUnread = newAppt.unreadMessagesCount || 0;
                            if (newUnread > prevUnread) {
                                toast(`💬 New message on PO #${newAppt.customerPoNumber}`, { icon: '📩' });
                            }
                        }
                    });
                }

                prevAppointmentsRef.current = newAppts;
            }
        }
    );

    const appointments: Appointment[] = data?.appointments || [];

    // Automatically link any guest bookings created in this browser session
    useEffect(() => {
        if (!token || typeof window === 'undefined') return;
        try {
            const rawClaims = localStorage.getItem('baywhiz_guest_booking_claims_v1');
            if (!rawClaims) return;
            const parsedClaims = JSON.parse(rawClaims);
            if (!Array.isArray(parsedClaims) || parsedClaims.length === 0) return;

            api.claimCarrierAppointments({ claimTokens: parsedClaims })
                .then((res) => {
                    localStorage.removeItem('baywhiz_guest_booking_claims_v1');
                    if (res?.claimedCount > 0) {
                        toast.success(`Linked ${res.claimedCount} booking${res.claimedCount > 1 ? 's' : ''} to your account`);
                        mutate();
                    }
                })
                .catch(() => {
                    // Leave tokens for retry on next load if network failed
                });
        } catch {
            // Ignore malformed storage
        }
    }, [token, mutate]);

    const handleManualClaim = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!claimCode.trim()) {
            toast.error('Paste the signed Claim Token from your booking confirmation');
            return;
        }
        setClaiming(true);
        try {
            const res = await api.claimCarrierAppointments({
                claimToken: claimCode.trim(),
            });
            toast.success(res.claimedCount > 0 ? 'Booking linked to your account!' : 'Booking is already linked to your account.');
            setClaimCode('');
            setClaimPo('');
            setShowClaimForm(false);
            mutate();
        } catch (err: any) {
            toast.error(err.message || 'Could not link booking');
        } finally {
            setClaiming(false);
        }
    };

    const handleCancelAppointment = async (e: React.MouseEvent, appt: Appointment) => {
        e.stopPropagation();
        if (!window.confirm(`Cancel appointment request for PO #${appt.customerPoNumber}?`)) return;
        setCancellingId(appt.id);
        try {
            await api.cancelCarrierAppointment(appt.id, 'Cancelled from Carrier Portal');
            toast.success(`Cancelled request for PO #${appt.customerPoNumber}`);
            mutate();
        } catch (err: any) {
            toast.error(err.message || 'Failed to cancel appointment');
        } finally {
            setCancellingId(null);
        }
    };

    // Deep link support: auto-open modal from email link
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const action = params.get('action');
        const targetId = params.get('id');
        if (action === 'complete' && targetId && appointments.length > 0) {
            const target = appointments.find(a => a.id === targetId && a.status === 'pending' && a.agentDecision === 'missing_information');
            if (target) {
                setCompleteModalAppt(target);
                // Clean URL
                window.history.replaceState({}, '', window.location.pathname);
            }
        }
    }, [appointments]);

    // --- Derived State: Stats & Filtering ---
    const stats = useMemo(() => {
        const counts = {
            pending: 0,
            approved: 0,
            checked_in: 0,
            in_progress: 0,
            completed: 0,
            cancelled: 0,
            total: appointments.length
        };
        appointments.forEach(a => {
            if (counts[a.status] !== undefined) {
                counts[a.status]++;
            }
        });
        return counts;
    }, [appointments]);

    const filteredAppointments = useMemo(() => {
        return appointments.filter(appt => {
            // Text Search
            const searchLower = searchQuery.toLowerCase();
            const matchesSearch =
                !searchQuery ||
                String(appt.customerPoNumber).toLowerCase().includes(searchLower) ||
                String(appt.warehouseName).toLowerCase().includes(searchLower);

            // Status Filter (checked_in filter also includes in_progress / At Dock appointments)
            const matchesStatus =
                statusFilter === 'all' ||
                appt.status === statusFilter ||
                (statusFilter === 'checked_in' && appt.status === 'in_progress');

            return matchesSearch && matchesStatus;
        });
    }, [appointments, searchQuery, statusFilter]);


    // Robust Stepper State Helper
    const STEPS = ['pending', 'approved', 'checked_in', 'completed'];

    const isPastStatus = (status: string) => {
        const s = status.toLowerCase();
        return s === 'completed' || s === 'cancelled';
    };

    const upcomingList = statusFilter === 'all'
        ? filteredAppointments.filter(a => !isPastStatus(a.status))
        : filteredAppointments;

    const pastList = statusFilter === 'all'
        ? filteredAppointments.filter(a => isPastStatus(a.status))
        : [];

    const sectionHeading =
        statusFilter === 'completed' ? 'Completed Appointments'
            : statusFilter === 'cancelled' ? 'Declined & Cancelled Appointments'
                : statusFilter === 'pending' ? 'Awaiting Approval'
                    : statusFilter === 'approved' ? 'Approved Appointments'
                        : (statusFilter === 'checked_in' || statusFilter === 'in_progress') ? 'Checked-In Appointments'
                            : 'Upcoming Appointments';

    const normalizeStepStatus = (status: string) => {
        const s = status.toLowerCase();
        return s === 'in_progress' ? 'checked_in' : s;
    };

    const getStepProgress = (status: string) => {
        const idx = STEPS.indexOf(normalizeStepStatus(status));
        if (idx === -1) return 0; // Cancelled or unknown
        return Math.min(100, Math.max(0, (idx / (STEPS.length - 1)) * 100));
    };

    const getStepStatus = (currentStatus: string, stepId: string) => {
        const currentIdx = STEPS.indexOf(normalizeStepStatus(currentStatus));
        const stepIdx = STEPS.indexOf(stepId);

        if (currentStatus === 'cancelled') return 'cancelled';
        if (currentIdx >= stepIdx) return 'completed'; // Step is done
        if (currentIdx === stepIdx - 1) return 'next'; // Step is next
        return 'pending'; // Step is future
    };

    // Helper to get formatted status label
    const getStatusLabel = (status: string, appt?: Appointment) => {
        switch (status) {
            case 'pending': return 'Awaiting Approval';
            case 'approved': return 'Approved';
            case 'checked_in': return 'Checked In';
            case 'in_progress': return 'At Dock';
            case 'completed': return 'Completed';
            case 'cancelled': {
                const reasonText = (appt?.cancelReason || appt?.notes || '').toLowerCase();
                return reasonText.includes('cancelled by carrier') ? 'Cancelled by Carrier' : 'Declined';
            }
            default: return status.charAt(0).toUpperCase() + status.slice(1);
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'pending': return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
            case 'approved': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
            case 'checked_in':
            case 'in_progress': return 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300';
            case 'completed': return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300';
            case 'cancelled': return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
            default: return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300';
        }
    };

    const renderAppointmentCard = (appt: Appointment) => {
        const progressPct = getStepProgress(appt.status);

        return (
            <div
                key={appt.id}
                onClick={() => handleAppointmentClick(appt)}
                className="group bg-card border border-border rounded-lg shadow-sm hover:shadow-md hover:border-primary/40 transition-all duration-200 cursor-pointer relative overflow-hidden"
            >
                {/* Status Accent Bar */}
                <div className={`absolute left-0 top-0 bottom-0 w-1 transition-colors duration-300 ${appt.status === 'completed' ? 'bg-emerald-500' :
                    (appt.status === 'checked_in' || appt.status === 'in_progress') ? 'bg-blue-500' :
                        appt.status === 'approved' ? 'bg-primary' :
                            appt.status === 'cancelled' ? 'bg-red-500' : 'bg-slate-300 dark:bg-slate-700'
                    }`} />

                <div className="flex flex-col lg:flex-row gap-4 p-4 pl-6">
                    {/* Main Info */}
                    <div className="flex-1 space-y-3">
                        {/* Top Row: PO & Status Pill */}
                        <div className="flex items-start justify-between">
                            <div>
                                <div className="flex items-center gap-2.5 mb-1">
                                    <h4 className="text-base font-bold tracking-tight text-foreground group-hover:text-primary transition-colors">
                                        PO # {appt.customerPoNumber}
                                    </h4>
                                    <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide border border-transparent ${getStatusColor(appt.status)}`}>
                                        {getStatusLabel(appt.status, appt)}
                                    </span>
                                    {appt.unreadMessagesCount ? (
                                        <Badge variant="error" pulse size="sm" className="shadow-sm scale-90 origin-left">
                                            <MessageSquare className="w-3 h-3" /> {appt.unreadMessagesCount}
                                        </Badge>
                                    ) : null}
                                </div>
                                <div className="flex items-center text-xs text-muted-foreground">
                                    <MapPin className="w-3 h-3 mr-1.5 text-primary/70" />
                                    {appt.warehouseName}
                                </div>
                                {appt.status === 'pending' && (
                                    <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-1">
                                        The warehouse may approve, revise, or decline your requested time.
                                    </p>
                                )}
                            </div>

                            <div className="text-right">
                                <div className="flex items-center gap-3">
                                    <div className="flex flex-col items-end">
                                        <span className="text-xs font-semibold text-foreground">
                                            {formatDateInTimeZone(appt.confirmedDate || appt.requestedDate, appt.warehouseTimeZone || 'America/Los_Angeles')}
                                        </span>
                                        <span className="text-[10px] text-muted-foreground uppercase tracking-wide">
                                            {formatInWarehouseTimeZone(appt.confirmedDate || appt.requestedDate, appt.warehouseTimeZone || 'America/Los_Angeles', {
                                                hour: 'numeric',
                                                minute: '2-digit',
                                                hour12: true,
                                            })}
                                        </span>
                                    </div>
                                    <div className="p-1.5 bg-secondary/50 rounded-md text-primary/80">
                                        <Calendar className="w-3.5 h-3.5" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Visualization */}
                        {appt.status !== 'cancelled' ? (
                            <div className="relative pt-3 pb-1 px-1">
                                {/* Track */}
                                <div className="absolute top-[19px] left-[12.5%] right-[12.5%] h-0.5 bg-border rounded-full z-0" />

                                {/* Progress Fill - Dynamically Calculated */}
                                <div
                                    className="absolute top-[19px] left-[12.5%] h-0.5 bg-primary rounded-full z-0 transition-all duration-700 ease-out shadow-sm shadow-primary/30"
                                    style={{
                                        width: `${progressPct * 0.75}%`
                                    }}
                                />

                                <div className="relative z-10 grid grid-cols-4 gap-4">
                                    {STEPS.map((stepId) => {
                                        const status = getStepStatus(appt.status, stepId);
                                        const isCompleted = status === 'completed';
                                        const isCurrent = normalizeStepStatus(appt.status) === stepId;
                                        let label = stepId.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                                        if (stepId === 'pending') label = 'Requested';
                                        if (stepId === 'checked_in') label = appt.status === 'in_progress' ? 'At Dock' : 'Checked In';

                                        return (
                                            <div key={stepId} className="flex flex-col items-center gap-1.5 group/step">
                                                <div className={`
                                                    w-3.5 h-3.5 rounded-full flex items-center justify-center border transition-all duration-300
                                                    ${isCompleted || isCurrent
                                                        ? 'border-primary bg-card text-primary shadow-sm scale-110'
                                                        : 'border-border bg-muted/30 text-transparent'}
                                                `}>
                                                    {isCompleted ? (
                                                        <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                                                    ) : isCurrent ? (
                                                        <div className="w-1.5 h-1.5 bg-primary rounded-full animate-pulse" />
                                                    ) : (
                                                        <div className="w-1 h-1 rounded-full bg-muted-foreground/30" />
                                                    )}
                                                </div>
                                                <span className={`
                                                    text-[9px] font-bold text-center transition-all duration-200 uppercase tracking-wide
                                                    ${isCurrent
                                                        ? 'text-primary translate-y-0'
                                                        : isCompleted
                                                            ? 'text-foreground/70'
                                                            : 'text-muted-foreground/40'}
                                                `}>
                                                    {label}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ) : (
                            <div className="p-2.5 bg-red-50/50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/30 rounded-lg flex items-center gap-3">
                                <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
                                <div className="flex flex-col">
                                    <span className="text-xs font-bold text-red-700 dark:text-red-300">
                                        {(appt.cancelReason || appt.notes || '').toLowerCase().includes('cancelled by carrier')
                                            ? 'Appointment Cancelled by Carrier'
                                            : 'Appointment Declined'}
                                    </span>
                                    {(appt.cancelReason || appt.notes) && (
                                        <span className="text-[11px] text-red-600/80 dark:text-red-300/80">
                                            {appt.cancelReason || appt.notes}
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}
                        {(appt.status === 'pending' || appt.status === 'approved') && (
                            <div className="flex justify-end pt-1">
                                <button
                                    type="button"
                                    disabled={cancellingId === appt.id}
                                    onClick={(e) => handleCancelAppointment(e, appt)}
                                    className="text-xs font-medium text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 px-2.5 py-1 rounded border border-red-200 dark:border-red-900/40 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                                >
                                    {cancellingId === appt.id ? 'Cancelling...' : 'Cancel Request'}
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Action Arrow (Desktop) */}
                    <div className="hidden lg:flex items-center justify-center pl-6 border-l border-border/50">
                        <div className="p-1.5 rounded-full text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary transform group-hover:translate-x-1 transition-all duration-300">
                            <ChevronRight className="w-4 h-4" />
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    if (isLoading) {
        return (
            <div className="w-full max-w-none px-4 md:px-8 py-6 space-y-8">
                <div className="max-w-6xl mr-auto space-y-8">
                    <div className="flex justify-between items-center animate-pulse">
                        <div className="h-9 w-48 bg-muted rounded-lg" />
                        <div className="h-10 w-40 bg-muted rounded-lg" />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                        {[1, 2, 3].map(i => <div key={i} className="h-24 bg-card rounded-xl animate-pulse" />)}
                    </div>
                    <div className="space-y-4">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="h-56 bg-card border border-border rounded-xl animate-pulse" />
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="w-full max-w-none px-4 md:px-8 py-6">
                <div className="max-w-6xl mr-auto flex flex-col items-center justify-center p-12 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-900 rounded-xl text-center">
                    <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
                    <h3 className="text-lg font-bold text-red-700 dark:text-red-400">Unable to load dashboard</h3>
                    <p className="text-sm text-red-600 dark:text-red-300 mb-4">There was a problem connecting to the server.</p>
                    <button onClick={() => window.location.reload()} className="btn btn-outline btn-sm border-red-300 text-red-600 hover:bg-red-100 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-900/20">
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full max-w-none px-4 md:px-8 py-6 text-foreground min-h-screen">
            {/* Inner Content Container - Left Aligned - Wider */}
            <div className="max-w-7xl mr-auto space-y-6">

                {/* Header Section */}
                <header className="flex flex-col md:flex-row md:items-center justify-between gap-6 pb-2 border-b border-border/40 pl-12 md:pl-0">
                    <div className="space-y-1">
                        <h1 className="text-3xl font-bold tracking-tight text-foreground">Your Appointments</h1>
                        <p className="text-muted-foreground text-sm md:text-base">
                            Request new appointments and track upcoming arrivals.
                        </p>
                        <p className="text-xs text-amber-600 dark:text-amber-500 font-medium">
                            All times are shown in the respective warehouse's local timezone.
                        </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <button
                            type="button"
                            className="btn btn-outline btn-sm"
                            onClick={() => setShowClaimForm((v) => !v)}
                        >
                            Link Guest Booking
                        </button>
                        <button
                            className="btn btn-primary shadow-lg shadow-primary/20 hover:shadow-primary/30 active:scale-95 transition-all duration-200"
                            onClick={() => setLocation('/carrier-request')}
                        >
                            <Plus className="w-4 h-4" /> Request Appointment
                        </button>
                        <button
                            type="button"
                            disabled={signingOut}
                            onClick={handleSignOut}
                            aria-label="Sign out"
                            className="btn btn-outline btn-sm text-muted-foreground hover:text-red-600 hover:border-red-300 dark:hover:text-red-400"
                        >
                            <LogOut className="w-4 h-4" /> {signingOut ? 'Signing Out...' : 'Sign Out'}
                        </button>
                    </div>
                </header>

                {showClaimForm && (
                    <form
                        onSubmit={handleManualClaim}
                        className="bg-card border border-border rounded-xl p-4 flex flex-col sm:flex-row items-stretch sm:items-end gap-3 shadow-sm"
                    >
                        <div className="flex-1">
                            <label className="block text-xs font-semibold text-muted-foreground mb-1">
                                Signed Claim Token *
                            </label>
                            <input
                                type="text"
                                value={claimCode}
                                onChange={(e) => setClaimCode(e.target.value)}
                                placeholder="Paste signed claim token issued at booking submission"
                                className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm font-mono"
                                required
                            />
                        </div>
                        <div className="flex gap-2">
                            <button
                                type="submit"
                                disabled={claiming}
                                className="btn btn-primary btn-sm px-4 py-2"
                            >
                                {claiming ? 'Linking...' : 'Link Booking'}
                            </button>
                            <button
                                type="button"
                                onClick={() => setShowClaimForm(false)}
                                className="btn btn-outline btn-sm px-3 py-2"
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                )}

                {/* Active Stats Row — only show cards with nonzero values */}
                {appointments.length > 0 && (() => {
                    const visibleStats = [
                        { label: 'Awaiting', value: stats.pending, color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50/50 dark:bg-amber-900/10', border: 'border-amber-100 dark:border-amber-900/30' },
                        { label: 'Approved', value: stats.approved, color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-50/50 dark:bg-blue-900/10', border: 'border-blue-100 dark:border-blue-900/30' },
                        { label: 'Checked In', value: stats.checked_in + stats.in_progress, color: 'text-indigo-600 dark:text-indigo-400', bg: 'bg-indigo-50/50 dark:bg-indigo-900/10', border: 'border-indigo-100 dark:border-indigo-900/30' },
                    ].filter(s => s.value > 0);

                    if (visibleStats.length === 0) return null;

                    return (
                        <div className={`grid gap-3`} style={{ gridTemplateColumns: `repeat(${Math.min(visibleStats.length, 4)}, minmax(0, 1fr))` }}>
                            {visibleStats.map(stat => (
                                <div key={stat.label} className={`rounded-lg border p-3 flex flex-col items-center justify-center text-center transition-all ${stat.bg} ${stat.border}`}>
                                    <span className={`text-xl font-bold ${stat.color}`}>{stat.value}</span>
                                    <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mt-0.5">{stat.label}</span>
                                </div>
                            ))}
                        </div>
                    );
                })()}

                {/* Needs Your Action — Missing Information */}
                {(() => {
                    const needsAction = appointments.filter(
                        a => a.status === 'pending' && (a.agentDecision === 'missing_information' || a.agentDecision === 'waiting_on_carrier')
                    );
                    if (needsAction.length === 0) return null;
                    return (
                        <div className="bg-amber-50/80 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/40 rounded-xl p-4 space-y-3">
                            <div className="flex items-center gap-2">
                                <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                                <h3 className="text-sm font-bold text-amber-800 dark:text-amber-300">Action Required — Complete Your Request</h3>
                            </div>
                            <p className="text-xs text-amber-700 dark:text-amber-400">
                                The warehouse needs more information before your appointment can be processed.
                            </p>
                            <div className="space-y-2">
                                {needsAction.map(appt => (
                                    <div key={appt.id} className="flex items-center justify-between bg-white/60 dark:bg-card/60 rounded-lg border border-amber-100 dark:border-amber-900/30 p-3">
                                        <div className="flex-1 min-w-0">
                                            <span className="text-sm font-semibold text-foreground">PO #{appt.customerPoNumber}</span>
                                            <span className="text-xs text-muted-foreground ml-2">{appt.warehouseName}</span>
                                        </div>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); setCompleteModalAppt(appt); }}
                                            className="btn btn-sm bg-amber-600 hover:bg-amber-700 text-white shadow-sm whitespace-nowrap"
                                        >
                                            Complete Request
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })()}

                {/* Controls Section */}
                {appointments.length > 0 && (
                    <div className="flex flex-col sm:flex-row gap-3 items-center">
                        <div className="relative flex-1 w-full sm:w-auto">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                            <input
                                type="text"
                                placeholder="Search PO # or Warehouse..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-9 pr-8 py-1.5 bg-card border border-border rounded-md text-sm focus:ring-2 focus:ring-primary/20 outline-none transition-all shadow-sm"
                            />
                            {searchQuery && (
                                <button
                                    type="button"
                                    aria-label="Clear search"
                                    onClick={() => setSearchQuery('')}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground hover:text-foreground"
                                >
                                    ✕
                                </button>
                            )}
                        </div>
                        <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0 no-scrollbar w-full sm:w-auto">
                            {[
                                { id: 'all', label: 'All' },
                                { id: 'pending', label: 'Awaiting' },
                                { id: 'approved', label: 'Approved' },
                                { id: 'checked_in', label: 'Checked In' },
                                { id: 'completed', label: 'Done' },
                                { id: 'cancelled', label: 'Declined' },
                            ].map(filter => (
                                <button
                                    key={filter.id}
                                    onClick={() => setStatusFilter(filter.id)}
                                    className={`
                                        px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide whitespace-nowrap transition-all border
                                        ${statusFilter === filter.id
                                            ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                                            : 'bg-card text-muted-foreground border-border hover:border-primary/50 hover:text-foreground'}
                                    `}
                                >
                                    {filter.label}
                                </button>
                            ))}
                        </div>
                        {(statusFilter !== 'all' || searchQuery) && (
                            <button
                                onClick={() => { setStatusFilter('all'); setSearchQuery(''); }}
                                className="text-xs text-muted-foreground hover:text-primary whitespace-nowrap px-2"
                            >
                                Clear
                            </button>
                        )}
                    </div>
                )}

                {/* Content List */}
                {filteredAppointments.length === 0 ? (
                    appointments.length === 0 ? (
                        /* Empty Filter Result (Total Empty) matches original 'No Active Appointments' view */
                        <div className="flex flex-col items-center justify-center py-12 px-4 bg-card border border-border border-dashed rounded-xl text-center">
                            <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                                <Truck className="w-7 h-7 text-primary" />
                            </div>
                            <h3 className="text-lg font-bold text-foreground mb-2">No appointments yet</h3>
                            <p className="text-muted-foreground max-w-xs mb-6 text-sm">
                                Request a dock appointment to get started.
                            </p>
                            <button
                                className="btn btn-primary px-6"
                                onClick={() => setLocation('/carrier-request')}
                            >
                                <Plus className="w-4 h-4" /> Request Appointment
                            </button>
                        </div>
                    ) : (
                        /* Empty Search Result */
                        <div className="py-12 text-center text-muted-foreground border border-dashed border-border rounded-xl">
                            <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
                            <p>No appointments match your filters.</p>
                            <button
                                onClick={() => { setSearchQuery(''); setStatusFilter('all'); }}
                                className="text-primary text-sm font-medium hover:underline mt-2"
                            >
                                Clear all filters
                            </button>
                        </div>
                    )
                ) : (
                    <div className="grid gap-6">
                        {upcomingList.length > 0 && (
                            <div className="grid gap-4">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-base font-semibold flex items-center gap-2 text-muted-foreground">
                                        <Calendar className="w-4 h-4" />
                                        {sectionHeading}
                                        <span className="px-1.5 py-0.5 bg-secondary text-secondary-foreground text-[10px] rounded-full font-mono">
                                            {upcomingList.length}
                                        </span>
                                    </h3>
                                </div>

                                <div className="grid gap-3">
                                    {upcomingList.map((appt) => renderAppointmentCard(appt))}
                                </div>
                            </div>
                        )}

                        {pastList.length > 0 && (
                            <div className="grid gap-4">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-base font-semibold flex items-center gap-2 text-muted-foreground">
                                        <CheckCircle className="w-4 h-4 text-emerald-500" />
                                        Completed & Past Appointments
                                        <span className="px-1.5 py-0.5 bg-secondary text-secondary-foreground text-[10px] rounded-full font-mono">
                                            {pastList.length}
                                        </span>
                                    </h3>
                                </div>

                                <div className="grid gap-3">
                                    {pastList.map((appt) => renderAppointmentCard(appt))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

                {/* Complete Missing Info Modal */}
                {completeModalAppt && (
                    <CompleteMissingInfoModal
                        appointment={completeModalAppt}
                        open={!!completeModalAppt}
                        onClose={() => setCompleteModalAppt(null)}
                        onSuccess={() => {
                            setCompleteModalAppt(null);
                            mutate(); // Refresh SWR data
                        }}
                    />
                )}
        </div>
    );
}
